import { useState, useMemo } from 'react';
import { skillTemplateService } from '@/services/skill-template-service';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
  Spinner,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  toast,
} from 'prosci-ui';
import { ChevronDown, X, SparkleIcon, TagIcon } from '@/lib/icons';
import type { SkillFormData, UpdateField } from './skill-form';

const STOPWORDS = new Set([
  'the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
  'should', 'may', 'might', 'can', 'shall', 'to', 'of', 'in', 'for',
  'on', 'with', 'at', 'by', 'from', 'as', 'into', 'through', 'during',
  'before', 'after', 'above', 'below', 'between', 'and', 'but', 'or',
  'not', 'no', 'so', 'if', 'then', 'than', 'too', 'very', 'just',
  'about', 'up', 'out', 'this', 'that', 'these', 'those', 'it', 'its',
  'also', 'each', 'which', 'their', 'them', 'they', 'user', 'guide',
  'help', 'helps', 'use', 'using', 'based', 'create', 'creates',
]);

type Props = {
  form: SkillFormData;
  updateField: UpdateField;
  showAdvanced: boolean;
  onShowAdvancedChange: (open: boolean) => void;
};

export function TriggerConfigCard({ form, updateField, showAdvanced, onShowAdvancedChange }: Props) {
  const [keywordInput, setKeywordInput] = useState('');
  const [naturalLanguageInput, setNaturalLanguageInput] = useState('');
  const [isGeneratingTriggers, setIsGeneratingTriggers] = useState(false);

  const currentKeywords = useMemo(
    () => form.trigger_keywords.split(',').map((s) => s.trim()).filter(Boolean),
    [form.trigger_keywords]
  );

  const addKeyword = (keyword: string) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    if (currentKeywords.some((k) => k.toLowerCase() === trimmed.toLowerCase())) return;
    const updated = [...currentKeywords, trimmed].join(', ');
    updateField('trigger_keywords', updated);
  };

  const removeKeyword = (index: number) => {
    const updated = currentKeywords.filter((_, i) => i !== index).join(', ');
    updateField('trigger_keywords', updated);
  };

  const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKeyword(keywordInput);
      setKeywordInput('');
    } else if (e.key === 'Backspace' && keywordInput === '' && currentKeywords.length > 0) {
      removeKeyword(currentKeywords.length - 1);
    }
  };

  // Frontend heuristic: suggest keywords from name/title/description text
  const suggestions = useMemo(() => {
    const sourceText = `${form.name} ${form.display_title} ${form.description}`.toLowerCase();
    const words = sourceText
      .split(/[\s\-_.,;:()]+/)
      .filter((w) => w.length > 2 && !STOPWORDS.has(w));

    const candidates = new Set<string>();
    words.forEach((w) => candidates.add(w));

    // Generate 2-word phrases
    const sourceWords = sourceText.split(/\s+/);
    for (let i = 0; i < sourceWords.length - 1; i++) {
      const w1 = sourceWords[i].replace(/[^a-z]/g, '');
      const w2 = sourceWords[i + 1].replace(/[^a-z]/g, '');
      if (w1.length > 2 && w2.length > 2 && !STOPWORDS.has(w1) && !STOPWORDS.has(w2)) {
        candidates.add(`${w1} ${w2}`);
      }
    }

    const existing = new Set(currentKeywords.map((k) => k.toLowerCase()));
    return Array.from(candidates)
      .filter((c) => !existing.has(c))
      .slice(0, 8);
  }, [form.name, form.display_title, form.description, currentKeywords]);

  const handleGenerateTriggers = async () => {
    if (!naturalLanguageInput.trim()) return;
    setIsGeneratingTriggers(true);
    try {
      const result = await skillTemplateService.generateTriggers({
        description: naturalLanguageInput,
        skill_name: form.name,
        skill_description: form.description,
      });
      // Merge generated keywords
      if (result.keywords?.length) {
        const existingKws = new Set(currentKeywords.map((k) => k.toLowerCase()));
        const newKws = result.keywords.filter((k) => !existingKws.has(k.toLowerCase()));
        if (newKws.length) {
          const merged = [...currentKeywords, ...newKws].join(', ');
          updateField('trigger_keywords', merged);
        }
      }
      // Merge generated patterns
      if (result.patterns?.length) {
        const existingPatterns = form.trigger_patterns
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean);
        const newPatterns = result.patterns.filter((p) => !existingPatterns.includes(p));
        if (newPatterns.length) {
          const merged = [...existingPatterns, ...newPatterns].join('\n');
          updateField('trigger_patterns', merged);
          onShowAdvancedChange(true);
        }
      }
      // Merge generated negative patterns
      if (result.negative_patterns?.length) {
        const existingNeg = form.negative_patterns
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean);
        const newNeg = result.negative_patterns.filter((p) => !existingNeg.includes(p));
        if (newNeg.length) {
          const merged = [...existingNeg, ...newNeg].join('\n');
          updateField('negative_patterns', merged);
          onShowAdvancedChange(true);
        }
      }
      toast.success('Trigger configuration generated');
      setNaturalLanguageInput('');
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : 'Failed to generate triggers');
    } finally {
      setIsGeneratingTriggers(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Trigger Configuration</CardTitle>
        <CardDescription>
          Define when this skill activates. Add keywords that users might say, or describe
          the intent in plain language.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Section 1: Keyword Tags (always visible, default view) */}
        <div>
          <label className="text-sm font-medium flex items-center gap-1.5">
            <TagIcon className="h-4 w-4" />
            Trigger Keywords
          </label>
          <p className="text-xs text-muted-foreground mt-0.5 mb-2">
            When a user's message contains any of these words or phrases, this skill will
            activate.
          </p>

          {/* Tag input area */}
          <div className="flex flex-wrap gap-1.5 min-h-[36px] p-2 border rounded-md bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1">
            {currentKeywords.map((keyword, index) => (
              <Badge key={index} variant="primary" tint className="gap-1 pr-1">
                {keyword}
                <button
                  type="button"
                  onClick={() => removeKeyword(index)}
                  className="ml-0.5 rounded-full hover:bg-primary/20 p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            <input
              type="text"
              className="flex-1 min-w-[120px] bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground"
              placeholder={
                currentKeywords.length === 0
                  ? 'Type a keyword and press Enter...'
                  : 'Add more...'
              }
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={handleKeywordKeyDown}
              onBlur={() => {
                if (keywordInput.trim()) {
                  addKeyword(keywordInput);
                  setKeywordInput('');
                }
              }}
            />
          </div>

          {/* Suggested keywords */}
          {suggestions.length > 0 && (
            <div className="mt-2">
              <span className="text-xs text-muted-foreground">Suggestions:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => addKeyword(suggestion)}
                    className="inline-flex items-center px-2 py-0.5 text-xs border border-dashed rounded-md text-muted-foreground hover:text-foreground hover:border-primary hover:bg-primary/5 transition-colors"
                  >
                    + {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Section 2: Natural Language AI Generation */}
        <div className="border rounded-lg p-4 bg-muted/30">
          <label className="text-sm font-medium flex items-center gap-1.5">
            <SparkleIcon className="h-4 w-4" />
            Generate with AI
          </label>
          <p className="text-xs text-muted-foreground mt-0.5 mb-2">
            Describe what should trigger this skill in plain language, and AI will generate
            keywords and patterns for you.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              className="flex-1 px-3 py-2 border rounded-md text-sm bg-background"
              placeholder='e.g. "When users ask to build a change management plan or create a CM strategy"'
              value={naturalLanguageInput}
              onChange={(e) => setNaturalLanguageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleGenerateTriggers();
                }
              }}
            />
            <Button
              variant="outline"
              onClick={handleGenerateTriggers}
              disabled={isGeneratingTriggers || !naturalLanguageInput.trim()}
            >
              {isGeneratingTriggers ? (
                <>
                  <Spinner className="h-4 w-4 mr-1" />
                  Generating...
                </>
              ) : (
                <>
                  <SparkleIcon className="h-4 w-4 mr-1" />
                  Generate
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Section 3: Advanced Options (Collapsible) */}
        <Collapsible open={showAdvanced} onOpenChange={onShowAdvancedChange}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform ${showAdvanced ? '' : '-rotate-90'}`}
              />
              Advanced Options
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-3">
            <div>
              <label className="text-sm font-medium">
                Regex Patterns
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 text-muted-foreground cursor-help">(?) </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-xs">
                      Regular expressions matched against user messages. Highest priority
                      (confidence 0.95). One pattern per line.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm font-mono"
                rows={3}
                placeholder={'build.*plan\ncreate.*plan\ngenerate.*plan'}
                value={form.trigger_patterns}
                onChange={(e) => updateField('trigger_patterns', e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">
                Negative Patterns
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 text-muted-foreground cursor-help">(?) </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-xs">
                      Messages matching these regex patterns will NOT trigger this skill,
                      even if they match positive patterns or keywords.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm font-mono"
                rows={2}
                placeholder="what is.*plan\nexplain.*plan"
                value={form.negative_patterns}
                onChange={(e) => updateField('negative_patterns', e.target.value)}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Document Skills (only relevant for deliverable mode) */}
        {form.execution_mode === 'deliverable' && (
          <div>
            <label className="text-sm font-medium">Document Skills (comma-separated)</label>
            <input
              type="text"
              className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
              placeholder="document_gen, web_search"
              value={form.document_skills}
              onChange={(e) => updateField('document_skills', e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Anthropic built-in skills to enable for document generation
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
